import React, { FC, useState, useMemo, useCallback } from 'react'
import { IntlProvider } from 'react-intl'
import { ThemeProvider } from 'styled-components'
import { theme, Loader } from '@elamajs/ui-kit'
import { getSvg } from '@src/containers/UtmGenerator/helpers'

import { usePartnersData } from '../../hooks'
import * as Styled from './PartnersList.styled'
import { getPartnerLogo } from '../../helpers'

const VISIBLE_CARDS_LIMIT = 5
const DEFAULT_TAB_ID = 1

const PartnersList: React.FC = () => {
  const { data, isFetching } = usePartnersData()
  const [activeTabId, setActiveTabId] = useState(DEFAULT_TAB_ID)
  const [isExpanded, setIsExpanded] = useState(false)

  const activeTab = useMemo(() => data?.find(tab => tab.categoryId === activeTabId), [
    activeTabId,
    data
  ])
  const allCards = activeTab?.cards ?? []
  const hasMore = allCards.length > VISIBLE_CARDS_LIMIT
  const visibleCards = useMemo(
    () => (isExpanded ? allCards : allCards.slice(0, VISIBLE_CARDS_LIMIT)),
    [isExpanded, allCards]
  )

  const handleTabClick = useCallback((tabId: number) => {
    setActiveTabId(tabId)
    setIsExpanded(false)
  }, [])

  const handleExpandClick = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  if (isFetching) {
    return <Loader />
  }

  return (
    <Styled.Block>
      <Styled.TabsWrapper>
        {data?.map(tab => (
          <Styled.Tab
            key={tab.categoryId}
            $isActive={tab.categoryId === activeTabId}
            onClick={() => handleTabClick(tab.categoryId)}
          >
            {tab.categoryName}
          </Styled.Tab>
        ))}
      </Styled.TabsWrapper>

      <Styled.CardsWrapper>
        {visibleCards.map((card, index) => {
          const isBig = index === 0
          const CardWrapper = isBig ? Styled.CardBig : Styled.CardSmall

          return (
            <CardWrapper key={index}>
              <Styled.CardContentLeft $isBig={isBig}>
                <Styled.CardLogoWrapper $isBig={isBig}>
                  <Styled.CardLogoImage
                    src={getPartnerLogo(card.logoIcon)}
                    alt={card.title}
                    loading="lazy"
                    $isBig={isBig}
                  />
                </Styled.CardLogoWrapper>
                {card.price && (
                  <Styled.CardLogoText>
                    <Styled.TextCrossed>{card.price} ₽ в год</Styled.TextCrossed>
                    <Styled.CardLogoTextBadge>Бесплатно</Styled.CardLogoTextBadge>
                  </Styled.CardLogoText>
                )}
              </Styled.CardContentLeft>

              <Styled.CardContentRight $isBig={isBig}>
                {isBig ? (
                  <Styled.CardContentRightTitle>
                    <Styled.CardContentLogo>
                      <Styled.CardContentLogoImage
                        src={getPartnerLogo(card.logoIcon)}
                        alt={card.title}
                        loading="lazy"
                      />
                    </Styled.CardContentLogo>
                    <Styled.CardTitleBig>{card.title}</Styled.CardTitleBig>
                    <svg width="20" height="27">
                      <use xlinkHref="#icon-fire-yellow" />
                    </svg>
                  </Styled.CardContentRightTitle>
                ) : (
                  <Styled.CardContentRightTitle>
                    <Styled.CardContentLogo>
                      <Styled.CardContentLogoImage
                        src={getPartnerLogo(card.logoIcon)}
                        alt={card.title}
                        loading="lazy"
                      />
                    </Styled.CardContentLogo>
                    <Styled.CardTitleSmall>{card.title}</Styled.CardTitleSmall>
                  </Styled.CardContentRightTitle>
                )}
                <Styled.CardContentRightSubtitle>
                  {card.description}
                </Styled.CardContentRightSubtitle>
              </Styled.CardContentRight>
            </CardWrapper>
          )
        })}
      </Styled.CardsWrapper>

      {hasMore && (
        <Styled.ShowMoreButton type="button" onClick={handleExpandClick}>
          {isExpanded ? getSvg('arrowUp') : getSvg('arrowDown')}
          {isExpanded ? 'Свернуть' : 'Показать все'}
        </Styled.ShowMoreButton>
      )}
    </Styled.Block>
  )
}

const withProviders = (Component: FC): FC => {
  const NewComponent: FC = () => {
    return (
      <ThemeProvider theme={theme}>
        <IntlProvider locale="ru">
          <Component />
        </IntlProvider>
      </ThemeProvider>
    )
  }

  return NewComponent
}

export default withProviders(PartnersList)
